// ============================================================
// Abonelik ve faturalama — kiracı seviyesinde (SaaS)
//
// Bu modül kurumun MinikPanel'e ödediği ücreti yönetir. Velinin
// kuruma ödediği okul ücreti ayrı bir sistemdir (domain/invoices.js)
// ve ikisi hiçbir yerde birbirine karışmaz.
//
// Fatura tutarı kayıtta saklanmaz — plan tarifesinden ve o dönemin
// kullanımından hesaplanır. Okul ücreti tarafındaki ilkenin aynısı.
// Tek istisna tahsil edilmiş tutardır; o gerçekleşmiş bir olaydır.
// ============================================================

import { BUGUN, DONEM } from "../config.js";
import { PLANLAR, PLAN_SIRASI } from "../data/plans.js";
import { donemEkle } from "../lib/fmt.js";
import { T, tumKiracilar } from "./context.js";

export { PLANLAR, PLAN_SIRASI };


const kiraciOf = tenant => tenant ?? T();

// ---------- Plan ----------

/**
 * Kiracının aboneliği — eksik alanlar tamamlanmış hâlde.
 *
 * `since` ve `renewsAt` her kayıtta bulunmuyor: tohum verisinde
 * vardı ama basvuruyuOnayla ile açılan gerçek kiracılarda yoktu ve
 * abonelikFaturalari() `a.since.slice()` derken sistem panelinin
 * TAMAMI çöküyordu. Tek bir eksik alan yüzünden panelin kapanması
 * kabul edilemez; burada tamamlıyoruz.
 *
 * `since` yoksa kurumun kuruluş tarihine, o da yoksa bugüne düşer —
 * her ikisi de "abonelik bugün başladı" demek, yani fatura üretimi
 * boş liste verir, patlamaz.
 */
export function abonelik(tenant) {
  const k = kiraciOf(tenant);
  const a = k.subscription;
  if (!a) return null;

  const since = a.since ?? k.createdAt ?? BUGUN;
  return {
    ...a,
    since,
    renewsAt: a.renewsAt ?? `${donemEkle(since.slice(0, 7), 1)}-01`,
  };
}

export function plan(tenant) {
  return PLANLAR[abonelik(tenant)?.planId] ?? PLANLAR.baslangic;
}

/** Abonelik gerçekten ücretlendiriliyor mu? */
export const ucretlendiriliyor = tenant => {
  const a = abonelik(tenant);
  return a?.status === "aktif";
};

/** Deneme süresi bitimine kalan gün; deneme değilse null. */
export function denemeKalan(tenant, bugun = BUGUN) {
  const a = abonelik(tenant);
  if (a?.status !== "deneme" || !a.trialEndsAt) return null;
  return Math.round(
    (new Date(a.trialEndsAt + "T00:00:00Z") - new Date(bugun + "T00:00:00Z")) / 86400000,
  );
}

// ---------- Kullanım ----------

/** Kiracının bugünkü kullanımı — fiyatlama ve limit kontrolü buradan. */
export function kullanim(tenant) {
  const k = kiraciOf(tenant);

  // Şube ağacı yüklüyse ondan sayıyoruz — kiracının kendi
  // kullanıcısında durum hep budur.
  //
  // Sistem operatörüne ağaç HİÇ yüklenmiyor: kişisel veri sınırı
  // gereği kiracı altındaki koleksiyonları okuyamıyor
  // (firestore.rules → kiraciDa). O zaman kiracı belgesindeki
  // `usage` toplamlarına düşüyoruz; onları limitleriDenetle
  // tetikleyicisi yazıyor ve sayı olduğu için kişisel veri değiller.
  //
  // Bu geri düşüş olmadan sistem paneli her yerde sıfır gösteriyordu
  // ve sıfır, "veriyi göremiyorum" ile "gerçekten sıfır" arasındaki
  // farkı sildiği için yanlış bilgiydi.
  if (!k.branches?.length && k.usage) {
    return {
      sube: k.usage.sube ?? 0,
      ogrenci: k.usage.ogrenci ?? 0,
      personel: k.usage.personel ?? 0,
    };
  }

  return {
    sube: k.branches.length,
    ogrenci: k.branches.reduce((a, b) => a + b.students.length, 0),
    personel: k.branches.reduce((a, b) => a + b.staff.length, 0),
  };
}

/**
 * Kiracının şube ağacı bu oturumda yüklü mü?
 *
 * Sistem operatöründe yüklü DEĞİL. Görünümler bunu sorup "0" ile
 * "göremiyorum"u ayırmalı — sıfır göstermek uydurma bilgidir.
 */
export const agacYuklu = tenant => Boolean(kiraciOf(tenant)?.branches?.length);

/**
 * Limit durumu. Aşım engellenmez ama görünür kılınır —
 * müşteriyi kapıda durdurmak yerine plan yükseltmeye yönlendirmek
 * hem ticari hem operasyonel olarak doğru davranış.
 */
export function limitDurumu(tenant) {
  const p = plan(tenant);
  const k = kullanim(tenant);

  return Object.entries(p.limitler).map(([alan, limit]) => {
    const olan = k[alan] ?? 0;
    const oran = limit ? Math.round((olan / limit) * 100) : null;
    return {
      alan,
      limit,
      olan,
      oran,
      sinirsiz: limit == null,
      yaklasiyor: limit != null && olan >= limit * 0.85 && olan <= limit,
      asildi: limit != null && olan > limit,
    };
  });
}

export const limitAsimiVar = tenant => limitDurumu(tenant).some(x => x.asildi);

/** Yeni kayıt açılabilir mi — limit aşımında uyarı metni döner. */
export function limitUyarisi(tenant, alan) {
  const d = limitDurumu(tenant).find(x => x.alan === alan);
  if (!d || d.sinirsiz) return null;
  if (d.asildi) {
    return `${d.limit} ${ALAN_ADI[alan]} sınırı aşıldı (${d.olan}). Plan yükseltilmeli.`;
  }
  if (d.olan === d.limit) {
    return `${ALAN_ADI[alan]} sınırına ulaşıldı (${d.limit}). Yeni kayıt için plan yükseltilmeli.`;
  }
  return null;
}

const ALAN_ADI = { sube: "şube", ogrenci: "öğrenci", personel: "personel" };

// ---------- Modül erişimi ----------

/**
 * Modül kiracının planında var mı?
 *
 * Bu bir GÜVENLİK sınırı değil, ticari sınırdır: rol yetkisi neyi
 * yapmaya izin verildiğini, plan neyin satın alındığını söyler.
 * İkisi de sağlanmadan ekran açılmaz (bkz. auth.gorebilir).
 *
 * Sunucu tarafında da uygulanması gerekir; plan kimliği custom
 * claim'e yazılıp Cloud Functions ile doğrulanmalıdır.
 */
export const modulAcik = (viewId, tenant) => plan(tenant).moduller.includes(viewId);

/** Bu modülü açan en ucuz plan — yükseltme önerisi için. */
export function moduluAcanPlan(viewId) {
  for (const id of PLAN_SIRASI) {
    if (PLANLAR[id].moduller.includes(viewId)) return PLANLAR[id];
  }
  return null;
}

// ---------- Ücret ----------

/**
 * Kiracının aylık abonelik ücreti.
 * Taban + (ilk şubeden sonraki şubeler) + öğrenci başına, sonra indirim.
 */
export function aylikUcret(tenant, kullanimOverride) {
  const p = plan(tenant);
  const a = abonelik(tenant);
  const k = kullanimOverride ?? kullanim(tenant);

  const taban = p.aylikTaban;
  const ekSube = Math.max(0, k.sube - 1) * p.subeBasina;
  const ogrenci = k.ogrenci * p.ogrenciBasina;
  const brut = taban + ekSube + ogrenci;

  const yillik = a?.billingCycle === "yillik";
  const donemIndirimPct = yillik ? p.yillikIndirimPct : 0;
  const musteriIndirimPct = a?.discountPct ?? 0;
  const toplamIndirimPct = donemIndirimPct + musteriIndirimPct;
  const indirim = Math.round((brut * toplamIndirimPct) / 100);

  return {
    taban,
    ekSube,
    ekSubeAdet: Math.max(0, k.sube - 1),
    ogrenci,
    ogrenciAdet: k.ogrenci,
    brut,
    donemIndirimPct,
    musteriIndirimPct,
    indirim,
    net: brut - indirim,
    yillik,
    yillikToplam: yillik ? (brut - indirim) * 12 : null,
  };
}

// ---------- Abonelik faturaları ----------

/**
 * Abonelik başlangıcından bugüne fatura listesi.
 * Elle yazılmış dönemler korunur, eksikler üretilir.
 */
export function abonelikFaturalari(tenant) {
  const k = kiraciOf(tenant);
  const a = abonelik(k);
  if (!a) return [];

  const yazili = new Map((k.subscriptionInvoices ?? []).map(f => [f.period, f]));
  const list = [];

  for (let p = a.since.slice(0, 7); p <= DONEM; p = donemEkle(p, 1)) {
    const mevcut = yazili.get(p);
    const ucret = aylikUcret(k);

    list.push({
      period: p,
      issuedAt: mevcut?.issuedAt ?? `${p}-01`,
      dueAt: mevcut?.dueAt ?? `${p}-15`,
      // Geçmiş dönemler kapanmış sayılır; içinde bulunulan dönem açık.
      paidAt: mevcut ? mevcut.paidAt : (p < DONEM ? `${p}-08` : null),
      method: mevcut?.method ?? (p < DONEM ? a.billing?.yontem ?? "Havale/EFT" : null),
      note: mevcut?.note ?? null,
      tutar: ucret.net,
      makbuzNo: `AB-${p.slice(0, 4)}-${p.slice(5, 7)}-${k.id.slice(0, 3).toUpperCase()}`,
    });
  }

  return list.reverse();
}

export function abonelikDurumu(tenant) {
  const k = kiraciOf(tenant);
  const faturalar = abonelikFaturalari(k);
  const acik = faturalar.filter(f => !f.paidAt);
  const gecikmis = acik.filter(f => f.dueAt < BUGUN);

  return {
    faturalar,
    acik,
    gecikmis,
    acikTutar: acik.reduce((a, f) => a + f.tutar, 0),
    gecikmisTutar: gecikmis.reduce((a, f) => a + f.tutar, 0),
  };
}

// ---------- Sistem geneli ----------

/** Sistem operatörü için tüm kiracıların abonelik özeti. */
export function sistemOzeti() {
  const satirlar = tumKiracilar().map(k => {
    const u = aylikUcret(k);
    const d = abonelikDurumu(k);
    return {
      kiraci: k,
      plan: plan(k),
      abonelik: abonelik(k),
      kullanim: kullanim(k),
      ucret: u,
      acikTutar: d.acikTutar,
      gecikmisTutar: d.gecikmisTutar,
      limitAsimi: limitAsimiVar(k),
      denemeKalan: denemeKalan(k),
    };
  });

  return {
    satirlar: satirlar.sort((a, b) => b.ucret.net - a.ucret.net),
    // MRR: yalnızca ücretlendirilen abonelikler. Deneme sayılmaz —
    // henüz gelir değil.
    mrr: satirlar.filter(x => x.abonelik?.status === "aktif")
      .reduce((a, x) => a + x.ucret.net, 0),
    denemede: satirlar.filter(x => x.abonelik?.status === "deneme").length,
    acikToplam: satirlar.reduce((a, x) => a + x.acikTutar, 0),
    gecikmisToplam: satirlar.reduce((a, x) => a + x.gecikmisTutar, 0),
  };
}
